import React, { useState, useEffect, useCallback } from 'react';
import { getBilan, urlExportBilan } from '../services/api';
import { useAuth } from '../services/AuthContext';
import BarreNav from '../components/BarreNav';
import { couleurTypo, styleTypo } from '../services/typologies';

const MOIS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
const JOURS = ['dim','lun','mar','mer','jeu','ven','sam'];

function iso(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateCourte(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  if (!y) return s;
  return `${JOURS[new Date(y, m - 1, d).getDay()]}. ${d} ${MOIS[m - 1].slice(0, 4)}.`;
}

function libellePeriode(debut, fin) {
  if (debut === fin) {
    const [y, m, d] = debut.split('-').map(Number);
    return `${d} ${MOIS[m - 1]} ${y}`;
  }
  const [y1, m1, d1] = debut.split('-').map(Number);
  const [y2, m2, d2] = fin.split('-').map(Number);
  if (y1 === y2 && m1 === m2) return `${d1} – ${d2} ${MOIS[m1 - 1]} ${y1}`;
  if (y1 === y2) return `${d1} ${MOIS[m1 - 1]} – ${d2} ${MOIS[m2 - 1]} ${y1}`;
  return `${d1} ${MOIS[m1 - 1]} ${y1} – ${d2} ${MOIS[m2 - 1]} ${y2}`;
}

/** Périodes proposées d'un geste, les plus demandées en premier. */
function raccourcis() {
  const a = new Date();
  const moisEnCours = [new Date(a.getFullYear(), a.getMonth(), 1), new Date(a.getFullYear(), a.getMonth() + 1, 0)];
  const moisDernier = [new Date(a.getFullYear(), a.getMonth() - 1, 1), new Date(a.getFullYear(), a.getMonth(), 0)];
  const sept = new Date(a); sept.setDate(a.getDate() - 6);
  const trente = new Date(a); trente.setDate(a.getDate() - 29);
  return [
    { nom: 'Mois en cours', debut: iso(moisEnCours[0]), fin: iso(moisEnCours[1]) },
    { nom: 'Mois dernier',  debut: iso(moisDernier[0]), fin: iso(moisDernier[1]) },
    { nom: '7 derniers jours',  debut: iso(sept),  fin: iso(a) },
    { nom: '30 derniers jours', debut: iso(trente), fin: iso(a) },
  ];
}

const COULEUR_RESULTAT = {
  CLEARED:             { bg: 'var(--succes-fond)', fg: 'var(--succes-texte)', bord: 'var(--succes-bord)' },
  FAILED:              { bg: 'var(--danger-fond)', fg: 'var(--danger-texte)', bord: 'var(--danger-bord)' },
  'À venir':           { bg: 'var(--info-fond)', fg: 'var(--accent)', bord: 'var(--info-bord)' },
  'Sans compte rendu': { bg: 'var(--danger-fond)', fg: 'var(--danger-texte)', bord: 'var(--danger-bord)' },
};
const COULEUR_STATUT = {
  'Validé':                   { bg: 'var(--succes-fond)', fg: 'var(--succes-texte)' },
  'En attente de validation': { bg: 'var(--attention-fond)', fg: 'var(--attention-texte)' },
  'Rejeté':                   { bg: 'var(--danger-fond)', fg: 'var(--danger-texte)' },
  'Brouillon':                { bg: 'var(--surface-3)', fg: 'var(--texte-doux)' },
  'À venir':                  { bg: 'var(--info-fond)', fg: 'var(--accent)' },
  'Aucun compte rendu':       { bg: 'var(--danger-fond)', fg: 'var(--danger-texte)' },
};

function Pastille({ texte, palette }) {
  const c = palette[texte] || { bg: 'var(--surface-3)', fg: 'var(--texte-doux)' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
      background: c.bg, color: c.fg, border: `1px solid ${c.bord || 'transparent'}`,
      whiteSpace: 'nowrap',
    }}>{texte}</span>
  );
}

/**
 * Répartition en barres horizontales, triée du plus fréquent au moins.
 * `parTypo` reprend les teintes de famille du planning, pour que le même type
 * d'intervention porte la même couleur d'un écran à l'autre.
 */
function Repartition({ titre, donnees, total, parTypo = false }) {
  const lignes = Object.entries(donnees || {}).sort((a, b) => b[1] - a[1]);
  if (!lignes.length) return null;
  return (
    <div className="section-card">
      <div className="section-label">{titre}</div>
      {lignes.map(([nom, n]) => (
        <div key={nom} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3, gap: 8 }}>
            {parTypo
              ? <span style={styleTypo(nom)}>{nom}</span>
              : <span style={{ color: 'var(--texte)' }}>{nom}</span>}
            <span style={{ color: 'var(--texte-doux)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {n} <span style={{ color: 'var(--texte-faible)' }}>· {Math.round(n / total * 100)} %</span>
            </span>
          </div>
          <div style={{ height: 5, background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${n / total * 100}%`, height: '100%',
              background: parTypo ? couleurTypo(nom).texte : 'var(--accent)',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Bilan() {
  const { user } = useAuth();
  const defaut = raccourcis()[0];
  const [debut, setDebut] = useState(defaut.debut);
  const [fin, setFin] = useState(defaut.fin);
  const [bilan, setBilan] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);

  const charger = useCallback((d, f) => {
    setChargement(true);
    setErreur('');
    getBilan(d, f)
      .then(setBilan)
      .catch(() => { setErreur("Le bilan n'a pas pu être chargé. Réessayez dans un instant."); setBilan(null); })
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(debut, fin); }, [debut, fin, charger]);

  // Le téléchargement passe par fetch : l'en-tête d'authentification ne peut
  // pas voyager dans un simple lien.
  const exporter = async () => {
    setExportEnCours(true);
    setErreur('');
    try {
      const r = await fetch(urlExportBilan(debut, fin), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('export');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Interventions_${debut}_${fin}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      setErreur("Le fichier n'a pas pu être généré. Réessayez dans un instant.");
    } finally {
      setExportEnCours(false);
    }
  };

  const styleDate = {
    flex: 1, minWidth: 0, padding: '9px 10px', fontSize: 16, borderRadius: 8,
    border: '1px solid var(--bord-fort)', background: 'var(--surface)', color: 'var(--texte)',
  };

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="topbar-title">Mes interventions</div>
        <div style={{ fontSize: 13, color: 'var(--texte-doux)' }}>{user?.prenom} {user?.nom}</div>
      </div>

      <div className="content">
        {/* Choix de la période */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none' }}>
          {raccourcis().map(r => {
            const actif = r.debut === debut && r.fin === fin;
            return (
              <button key={r.nom} onClick={() => { setDebut(r.debut); setFin(r.fin); }} style={{
                flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 20,
                border: `1px solid ${actif ? 'var(--accent)' : 'var(--bord-fort)'}`,
                background: actif ? 'var(--accent-plein)' : 'var(--surface)', color: actif ? '#fff' : 'var(--accent)', cursor: 'pointer',
              }}>{r.nom}</button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
          <input type="date" value={debut} max={fin} onChange={e => e.target.value && setDebut(e.target.value)} style={styleDate} />
          <span style={{ color: 'var(--texte-doux)', fontSize: 13, flexShrink: 0 }}>au</span>
          <input type="date" value={fin} min={debut} onChange={e => e.target.value && setFin(e.target.value)} style={styleDate} />
        </div>

        {erreur && (
          <div style={{ background: 'var(--danger-fond)', color: 'var(--danger-texte)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
            {erreur}
          </div>
        )}

        {chargement ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--texte-doux)' }}>Chargement…</div>
        ) : !bilan ? null : bilan.total === 0 ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--texte-faible)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
            <div>Aucune intervention sur cette période</div>
          </div>
        ) : (
          <>
            {/* Total et export */}
            <div style={{
              background: 'var(--info-fond)', border: '1px solid var(--info-bord)', borderRadius: 12,
              padding: '14px 16px', marginBottom: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--accent)', lineHeight: 1 }}>{bilan.total}</div>
                <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
                  intervention{bilan.total > 1 ? 's' : ''} — {libellePeriode(bilan.debut, bilan.fin)}
                </div>
                {bilan.realisees !== bilan.total && (
                  <div style={{ fontSize: 11, color: 'var(--info-texte)', marginTop: 3 }}>
                    dont <b>{bilan.realisees}</b> avec compte rendu
                  </div>
                )}
              </div>
              <button onClick={exporter} disabled={exportEnCours} style={{
                flexShrink: 0, fontSize: 13, fontWeight: 600, padding: '10px 14px', borderRadius: 8,
                border: 'none', background: exportEnCours ? 'var(--info-bord)' : 'var(--accent-plein)', color: '#fff',
                cursor: exportEnCours ? 'default' : 'pointer',
              }}>
                {exportEnCours ? 'Génération…' : '⬇ Exporter'}
              </button>
            </div>

            <Repartition titre="Résultat" donnees={bilan.par_resultat} total={bilan.total} />
            <Repartition titre="Type d'intervention" donnees={bilan.par_type} total={bilan.total} parTypo />
            <Repartition titre="Statut de clôture" donnees={bilan.par_statut} total={bilan.total} />

            <div className="section-label" style={{ marginTop: 16, marginBottom: 8 }}>Détail</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bilan.interventions.map(l => (
                <div key={l.id + l.ref_work_order} className="card" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                      {dateCourte(l.date_intervention)}
                    </span>
                    <Pastille texte={l.resultat} palette={COULEUR_RESULTAT} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--texte)', marginBottom: 4 }}>
                    {l.client_final || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--texte-doux)', fontFamily: 'ui-monospace, monospace', marginBottom: 6 }}>
                    {[l.ref_commande, l.ref_work_order].filter(Boolean).join('  ·  ') || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {l.typo_raccordement && (
                      <span style={styleTypo(l.typo_raccordement)}>{l.typo_raccordement}</span>
                    )}
                    <Pastille texte={l.statut_cloture} palette={COULEUR_STATUT} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ height: 16 }} />
      </div>

      <BarreNav actif="bilan" />
    </div>
  );
}
